// How the Excel report's cells look, the same on every sheet - the design in Designforslag_Week43 (colours.js has the
// colours and lines):
// - each header in its category's colour, white bold text (black on the planner's grey), centred and wrapped;
// - a thin light grey grid; a medium grey line left of each new category (header included), a medium dark line above
//   each new CO; the rows white and light grey in turn for each new CO;
// - each task cell in its status's colour (cellStatus.js);
// - on a work sheet the sheet's own next task framed black, in bold;
// - where the customer's script had its red rule (scriptRedRule), a red frame over the status colour.
import { CLOSED_STATUSES, STATUS } from './cellStatus';
import { APPLICATION_LETTERS, VISIT_NUMBERS } from './taskCells';
import {
  CATEGORY_BORDER, CO_BORDER, GRID_BORDER, GROUP_FILLS, HEADER_BOTTOM_BORDER, HEADER_FONT, HEADER_SIDE_BORDER,
  NEXT_TASK_BORDER, NO_TASK_SHADES, PLANNER_FILL, PLANNER_HEADER_FONT, ROW_SHADES, STATUS_COLOURS, TAB_COLOURS,
  WARNING_BORDER,
} from './colours';

const GROUPS = {
  identity: ['CO', 'CustomerNo', 'Protocol', 'Trial'],
  people: ['SD', 'PI', 'FT'],
  info: [
    'LocatIonTarget', 'Host', 'Trialaddress', 'TreatmentCount', 'ReplicationCount', 'EarthSample', 'PlotSizeL',
    'PlotSizeW', 'Country', 'Area', 'Crop', 'Type', 'PosGPSLongitude', 'PosGPSLatitude',
  ],
  planner: ['prod', 'papir', 'person'],
  setup: ['Reserved area', 'Stacked out', 'Borders sprayed', 'Plot size'],
  applications: APPLICATION_LETTERS,
  assessments: VISIT_NUMBERS,
  finish: ['Harvest', 'Pegs up', 'Destruction'],
};
// The categories of task columns
const TASK_GROUPS = ['setup', 'applications', 'assessments', 'finish'];

/** A column's category by its header (a key of GROUP_FILLS); every column of the report has one. */
export function columnGroup(header) {
  return Object.keys(GROUPS).find((group) => GROUPS[group].includes(header)) ?? 'info';
}

// In the finish category, but the trial's flags (TRUE / FALSE), not tasks
const FLAGS = ['Harvest', 'Destruction'];

/** Whether a column holds tasks (set-up, A-L, V00-V14, finish; not the Harvest and Destruction flags). */
export const isTaskColumn = (header) => TASK_GROUPS.includes(columnGroup(header)) && !FLAGS.includes(header);

/** For each header: whether it starts a new category (the medium grey line left of it). */
export const categoryStarts = (headers) => headers.map((h, c) => c > 0 && columnGroup(h) !== columnGroup(headers[c - 1]));

/** A column that can hold a date is at least this wide, so a date never shows as #####. */
export const MIN_DATE_WIDTH = 11.5;
// The design's width 9 as Excel writes it: ExcelJS leaves out a width of exactly 9 (its default), and Excel then
// shows 8.43
export const NINE = 9.140625;
// Each column's width by its header, the same on every sheet (the design's); a task column at least MIN_DATE_WIDTH
const WIDTHS = {
  CO: 8, CustomerNo: 13, Protocol: 10, Trial: 22, PI: 8, FT: 8, SD: 8, LocatIonTarget: 16, Host: 20, Trialaddress: 24,
  'Reserved area': 11, 'Stacked out': 11, 'Borders sprayed': 12, 'Plot size': 11, TreatmentCount: 16,
  ReplicationCount: 17, EarthSample: 13, PlotSizeL: 11, PlotSizeW: 11, Country: NINE, Area: 8, Crop: NINE, Type: 14,
  Harvest: NINE, 'Pegs up': NINE, Destruction: 13, PosGPSLongitude: 14, PosGPSLatitude: 14, prod: 8, papir: 8, person: 8,
};

/** A column's width by its header. */
export const columnWidth = (header) => (isTaskColumn(header) ? Math.max(WIDTHS[header] ?? 0, MIN_DATE_WIDTH) : WIDTHS[header]);

export const solid = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb }, bgColor: { argb } });
export const FONT = { name: 'Calibri', size: 10 };
export const HEADER_HEIGHT = 32;
export const DATE_FORMAT = 'dd-mm-yyyy';

/**
 * A header cell: its category's colour, white bold text (black on the planner's grey), centred and wrapped, thin white
 * lines between, a medium dark line under it - and the medium grey line left of it when it starts a new category.
 * The category is the header's (columnGroup), unless given (the layout sheet's headers are the app's labels).
 */
export function styleHeaderCell(cell, categoryStart = false, group = columnGroup(cell.value)) {
  cell.fill = solid(GROUP_FILLS[group]);
  cell.font = { ...FONT, bold: true, color: { argb: group === 'planner' ? PLANNER_HEADER_FONT : HEADER_FONT } };
  cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
  cell.border = { left: categoryStart ? CATEGORY_BORDER : HEADER_SIDE_BORDER, right: HEADER_SIDE_BORDER, bottom: HEADER_BOTTOM_BORDER };
}

/**
 * For each column of the given categories: whether it starts a new category (as categoryStarts, by category).
 */
export const groupStarts = (groups) => groups.map((g, c) => c > 0 && g !== groups[c - 1]);

/**
 * A sheet's header row (row 1) with these headers, each column in its width. `groups` and `widths` (one per header)
 * replace the template's categories and widths by header.
 */
export function addHeaderRow(sheet, headers, { groups = null, widths = null } = {}) {
  const starts = groups ? groupStarts(groups) : categoryStarts(headers);
  headers.forEach((header, c) => { sheet.getColumn(c + 1).width = widths ? widths[c] : columnWidth(header); });
  const row = sheet.getRow(1);
  row.values = headers;
  row.eachCell((cell, c) => styleHeaderCell(cell, starts[c - 1], groups ? groups[c - 1] : columnGroup(cell.value)));
  row.height = HEADER_HEIGHT;
}

/**
 * A data sheet's set-up: the header row and its first `frozenColumns` columns frozen, zoom 90, its tab colour (by its
 * name, unless given), and printed landscape on A3, one page wide, the header row on every page.
 */
export function setUpSheet(sheet, frozenColumns, tabColour = TAB_COLOURS[sheet.name]) {
  sheet.views = [{ state: 'frozen', xSplit: frozenColumns, ySplit: 1, zoomScale: 90 }];
  sheet.properties.tabColor = { argb: tabColour };
  sheet.pageSetup = {
    ...sheet.pageSetup, paperSize: 8, orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0, printTitlesRow: '1:1',
  };
}

/**
 * The rows' CO groups, in order: for each row its shade (0 white, 1 light grey - in turn for each new CO) and whether
 * it starts a new CO (the medium dark line above it; not the first row, under the header).
 */
export function coGroups(cos) {
  let shade = 0;
  return cos.map((co, r) => {
    const coStart = r > 0 && String(co ?? '') !== String(cos[r - 1] ?? '');
    if (coStart) shade = 1 - shade;
    return { shade, coStart };
  });
}

/** A data cell's colour without a status: the row's shade; lighter in a task column; the planner's columns white. */
export function fallbackFill(header, shade) {
  if (columnGroup(header) === 'planner') return PLANNER_FILL;
  return isTaskColumn(header) ? NO_TASK_SHADES[shade] : ROW_SHADES[shade];
}

// Control characters (C0, but tab, LF and CR) are not allowed in the file's XML: one makes Excel call it unreadable
// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTERS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g;

/** A cell value as the file may hold it: text without control characters (other values as they are). */
export const cleanCellValue = (value) => (typeof value === 'string' ? value.replace(CONTROL_CHARACTERS, '') : value);

/**
 * A data cell with its value and lines: the grid, the category line left of it and the CO line above it where they
 * are; a date DD-MM-YYYY. A task cell (`task`) wraps its text and is centred vertically - it can hold "P ..." and
 * "TI ..." on two lines; the row height is left to Excel, which fits it to the text.
 */
export function setDataCell(cell, value, { categoryStart = false, coStart = false, task = false } = {}) {
  cell.value = cleanCellValue(value);
  cell.font = { ...FONT };
  cell.border = {
    left: categoryStart ? CATEGORY_BORDER : GRID_BORDER, right: GRID_BORDER, top: coStart ? CO_BORDER : GRID_BORDER, bottom: GRID_BORDER,
  };
  if (task) cell.alignment = { vertical: 'middle', wrapText: true };
  if (value instanceof Date) cell.numFmt = DATE_FORMAT;
}

const blank = (value) => value === null || value === undefined || String(value).trim() === '';

/**
 * EarthSample is no task, but empty it is missing info, coloured as such (a trial needs its soil sample); its
 * status object, or null.
 */
export const earthSampleStatus = (value) => (blank(value) ? { status: STATUS.missing } : null);

/**
 * The next task in a run of cells (their indexes, in order): the first open task - one that is not Done, Cancelled
 * or Not needed - after the last done one; with none done, the first open task. A cell without a task is passed
 * over. `statuses` are the row's cell statuses (cellStatus). Its index, or null.
 */
export function nextTaskIndex(indexes, statuses) {
  const lastDone = indexes.findLastIndex((i) => statuses[i]?.status === STATUS.done);
  const open = indexes.slice(lastDone + 1).find((i) => statuses[i]?.status && !CLOSED_STATUSES.includes(statuses[i].status));
  return open ?? null;
}

/**
 * The customer's script's red rule (Work_Sorting.py l.214-240), on a row by its headers: Stacked out not done while
 * EarthSample or the trial address is filled in - unlike the script, only when the trial has a Stacked out task (its
 * status in `statuses`); EarthSample empty while the trial address is filled in. Returns the indexes of those cells.
 * How they are shown is SCRIPT_RED_RULE_SHOWN_AS.
 */
export function scriptRedRule(headers, values, statuses) {
  const at = (header) => headers.indexOf(header);
  const value = (header) => values[at(header)];
  const cells = [];
  const address = !blank(value('Trialaddress'));
  const stackedOut = at('Stacked out') >= 0 && Boolean(statuses[at('Stacked out')]);
  if (stackedOut && !(value('Stacked out') instanceof Date) && (address || !blank(value('EarthSample')))) cells.push(at('Stacked out'));
  if (at('EarthSample') >= 0 && blank(value('EarthSample')) && address) cells.push(at('EarthSample'));
  return cells;
}

/**
 * How the script's red rule is shown: 'frame' - a red frame over the cell's status colour (agreed); 'overdue' - the
 * cell in the Overdue colour instead; 'none' - not at all.
 */
export const SCRIPT_RED_RULE_SHOWN_AS = 'frame';

export const frame = (side) => ({ top: side, left: side, bottom: side, right: side });

/**
 * Gives a data cell (already set up with setDataCell) the report's look: its status colour (fill, text colour, struck
 * through for Cancelled) or `fallbackFill`, the next task's bold text and black frame, else the red rule's frame.
 */
export function styleCell(cell, { status = null, next = false, warning = false, fallbackFill: fill = null } = {}) {
  const shown = warning && SCRIPT_RED_RULE_SHOWN_AS === 'overdue' ? STATUS.overdue : status;
  const colours = shown ? STATUS_COLOURS[shown] : null;
  const argb = colours?.fill ?? fill;
  if (argb) cell.fill = solid(argb);
  const font = { ...cell.font };
  if (colours?.font) font.color = { argb: colours.font };
  if (colours?.strike) font.strike = true;
  if (next) font.bold = true;
  cell.font = font;
  // The sheet's next task wins the frame, as in the design (Stacked out on Set-Up is often both)
  if (next) cell.border = frame(NEXT_TASK_BORDER);
  else if (warning && SCRIPT_RED_RULE_SHOWN_AS === 'frame') cell.border = frame(WARNING_BORDER);
}
